// Inline SVG glyphs used across the event-log redesign. Stroke-based, low chrome.
// lucide-react is used elsewhere in the app for chevrons / battery / user; the
// glyphs below are kept local because they appear in toned tiles where exact
// proportions matter for visual parity with the Direction-1 mock.
import type { SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

const base = (size = 16): SVGProps<SVGSVGElement> => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
});

export function ArrowDownIcon({ size = 12, ...rest }: IconProps) {
  return (
    <svg {...base(size)} strokeWidth={2.2} {...rest}>
      <path d="M12 5v14M5 12l7 7 7-7" />
    </svg>
  );
}

export function ArrowUpIcon({ size = 12, ...rest }: IconProps) {
  return (
    <svg {...base(size)} strokeWidth={2.2} {...rest}>
      <path d="M12 19V5M5 12l7-7 7 7" />
    </svg>
  );
}

export function ClockIcon({ size = 18, ...rest }: IconProps) {
  return (
    <svg {...base(size)} strokeWidth={1.6} {...rest}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

export function TrayIcon({ size = 18, ...rest }: IconProps) {
  return (
    <svg {...base(size)} strokeWidth={1.6} {...rest}>
      <path d="M3 8h18l-2 5H5L3 8Z" />
      <path d="M3 8 5 5h14l2 3" />
      <path d="M5 13v4h14v-4" />
    </svg>
  );
}

export function DispatchIcon({ size = 14, ...rest }: IconProps) {
  return (
    <svg {...base(size)} {...rest}>
      <path d="M2 12 22 4l-8 18-2-8-10-2Z" />
    </svg>
  );
}

export function TaskIcon({ size = 14, ...rest }: IconProps) {
  return (
    <svg {...base(size)} {...rest}>
      <polygon points="6,4 20,12 6,20" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function EStopIcon({ size = 14, ...rest }: IconProps) {
  return (
    <svg {...base(size)} {...rest}>
      <circle cx="12" cy="12" r="9" />
      <rect x="8" y="8" width="8" height="8" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function HandIcon({ size = 14, ...rest }: IconProps) {
  return (
    <svg {...base(size)} {...rest}>
      <path d="M6 11V6a2 2 0 1 1 4 0v5" />
      <path d="M10 11V4a2 2 0 1 1 4 0v7" />
      <path d="M14 11V5a2 2 0 1 1 4 0v9a7 7 0 0 1-14 0v-2a2 2 0 1 1 4 0" />
    </svg>
  );
}

export function ChartBarsIcon({ size = 16, ...rest }: IconProps) {
  return (
    <svg {...base(size)} strokeWidth={2.4} {...rest}>
      <path d="M3 21V8M9 21V4M15 21v-9M21 21V14" />
    </svg>
  );
}

export function ChevronLeftIcon({ size = 14, ...rest }: IconProps) {
  return (
    <svg {...base(size)} strokeWidth={2} {...rest}>
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

export function ChevronRightIcon({ size = 14, ...rest }: IconProps) {
  return (
    <svg {...base(size)} strokeWidth={2} {...rest}>
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}
